import * as React from 'react';
import { useState } from 'react';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { ChipButton } from '~shared/components/buttons/ChipButton';
import { Dialog } from '~shared/components/Dialog/Dialog';
import { Divider } from '~shared/components/Divider';
import Textarea from '~shared/components/Textarea/Textarea';
import { toast } from '~shared/components/Toast';

import { ActivityInsights } from '../../../../../api/activities/insights/insightSchema';

interface RateInsightsFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  insights: ActivityInsights;
}

type FeedbackReason =
  | 'too-generic'
  | 'too-detailed'
  | 'inaccurate'
  | 'not-relevant'
  | 'other';

const feedbackOptions: { value: FeedbackReason; label: string }[] = [
  { value: 'too-generic', label: 'Too generic' },
  { value: 'too-detailed', label: 'Too detailed' },
  { value: 'inaccurate', label: 'Inaccurate' },
  { value: 'not-relevant', label: 'Not relevant' },
  { value: 'other', label: 'Other' },
];

export function RateInsightsFeedbackDialog({
  open,
  onClose,
  insights,
}: RateInsightsFeedbackDialogProps) {
  const [selectedReasons, setSelectedReasons] = useState<FeedbackReason[]>([]);
  const [feedbackNote, setFeedbackNote] = useState('');

  const toggleReason = (reason: FeedbackReason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const handleSubmit = () => {
    sendTeacherAmplitudeEvent(EventTypes.RateInsights, {
      insight_rating: 'not-helpful',
      insight_details: {
        insight_id: insights.id,
        insight_generation_number: insights.inputSettings.generationAttempt,
        ai_prompt_version: insights.inputSettings.promptVersion,
      },
      feedback_reasons: selectedReasons,
      feedback_note: feedbackNote || undefined,
    });

    toast(translate('Thanks for your feedback!'), { tone: 'positive' });

    setSelectedReasons([]);
    setFeedbackNote('');
    onClose();
  };

  const handleClose = () => {
    setSelectedReasons([]);
    setFeedbackNote('');
    onClose();
  };

  return (
    <Dialog
      id="rate-insights-container"
      open={open}
      onClose={handleClose}
      title={translate('Rate Insights')}
      showBackdrop
    >
      <div className="flex flex-col gap-4 mt-4 text-base text-blue-900">
        <Divider />
        <div>
          <label className="block mb-3">
            {translate('Provide Additional Feedback')}
          </label>
          <div
            className="flex flex-wrap gap-2 mr-18"
            role="group"
            aria-label={translate('Provide Additional Feedback')}
          >
            {feedbackOptions.map((option) => (
              <ChipButton
                key={option.value}
                isSelected={selectedReasons.includes(option.value)}
                onClick={() => toggleReason(option.value)}
              >
                {translate(option.label)}
              </ChipButton>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="feedback-note" className="block mb-2">
            {translate('Add a note (optional)')}
          </label>
          <Textarea
            id="feedback-note"
            value={feedbackNote}
            onChange={(e) => setFeedbackNote(e.target.value)}
            minRows={1}
            maxLength={1000}
            className="w-full"
          />
          <p className="text-xs mt-2">
            {translate('Please avoid sharing personal or sensitive info.')}
          </p>
        </div>

        <div className="flex justify-end">
          <SmallButton
            onClick={handleSubmit}
            isDisabled={selectedReasons.length === 0}
          >
            {translate('Submit')}
          </SmallButton>
        </div>
      </div>
    </Dialog>
  );
}
